/**
 * HustleMeetHostApplyPage.jsx — /meet/host/apply
 *
 * Simple 3-field application. Members submit → admin approves →
 * user can then create Circles via /host/mine.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Send, CheckCircle2 } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function HustleMeetHostApplyPage() {
  const [venueId,   setVenueId]   = useState("");
  const [venueName, setVenueName] = useState("");
  const [experience,setExperience]= useState("");
  const [apps,      setApps]      = useState(null);
  const [busy,      setBusy]      = useState(false);

  useEffect(() => {
    document.title = "HustleMeet · Host application";
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-circles/host-apps/mine`,
          { headers: authHeader(), withCredentials: true });
        setApps(data?.applications || []);
      } catch {
        setApps([]);
      }
    })();
  }, []);

  const submit = async () => {
    if (experience.trim().length < 20) {
      toast.error("Please share a little more (20+ characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hustlemeet-circles/host-apps`,
        {
          proposed_venue_id:   venueId,
          proposed_venue_name: venueName,
          experience,
        },
        { headers: authHeader(), withCredentials: true });
      toast.success("Application received. We'll email you within 48h.");
      setApps((apps || []).concat([data]));
      setExperience(""); setVenueName(""); setVenueId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  const pending = (apps || []).filter(a => a.status === "pending");
  const approved = (apps || []).filter(a => a.status === "approved");

  return (
    <div
      data-testid="hustlemeet-host-apply-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleMeet · Host apply", to: "/meet/host/apply" }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        <header className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Users size={22}/>
          </div>
          <div>
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              Become a host
            </p>
            <h1 className="mt-1 text-3xl font-black">Host your own HustleMeet Circle</h1>
            <p className="mt-2 text-sm text-stone-500">
              Run recurring dinners at your favourite venue. Earn Hustle Credit on every table you fill —
              spendable across HustleSuite.
            </p>
          </div>
        </header>

        {approved.length > 0 && (
          <div
            data-testid="host-approved-badge"
            className="rounded-2xl border border-emerald-300 dark:border-emerald-800 bg-emerald-500/10 p-5 mb-4 flex items-start gap-3"
          >
            <CheckCircle2 size={22} className="text-emerald-500 mt-0.5"/>
            <div>
              <p className="font-black">You&apos;re approved as a host</p>
              <p className="text-xs text-stone-500 mt-1">
                Head to your <a href="/meet/host/mine" className="text-amber-500 font-bold hover:underline">Circles dashboard</a> to publish your first dinner.
              </p>
            </div>
          </div>
        )}

        {pending.length > 0 && (
          <div
            data-testid="host-pending-badge"
            className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-amber-500/10 p-5 mb-4"
          >
            <p className="font-black">Application under review</p>
            <p className="text-xs text-stone-500 mt-1">
              We&apos;ll get back to you within 48h. Feel free to add another proposal below.
            </p>
          </div>
        )}

        <section className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">
              Proposed venue (Hustle vendor)
            </label>
            <input
              data-testid="input-proposed-venue-name"
              className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              value={venueName}
              onChange={(e) => setVenueName(e.target.value)}
              placeholder="e.g. Your Venue, South Brisbane"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">
              Vendor ID (if you know it)
            </label>
            <input
              data-testid="input-proposed-venue-id"
              className="mt-1 w-full h-11 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 text-sm"
              value={venueId}
              onChange={(e) => setVenueId(e.target.value)}
              placeholder="optional — we'll match by name too"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-bold">
              Your hosting experience
            </label>
            <textarea
              data-testid="input-experience"
              rows={5}
              className="mt-1 w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              placeholder={"What kinds of dinners will you host? How often? What's your existing community like?"}
            />
            <p className="mt-1 text-[10px] text-stone-500">
              Minimum 20 characters — we&apos;re looking for warm, inclusive hosts.
            </p>
          </div>
          <Button
            data-testid="btn-submit-host-app"
            onClick={submit}
            disabled={busy || experience.trim().length < 20}
            className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black h-11 px-6"
          >
            <Send size={14} className="mr-1"/>
            {busy ? "Submitting…" : "Submit application"}
          </Button>
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}
